import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowRight, Lock, GoogleLogo } from "@phosphor-icons/react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(loc.state?.googleError || "");
  const [loading, setLoading] = useState(false);
  const [showBreakGlass, setShowBreakGlass] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (!r.ok) {
      setErr(r.error || "Login failed");
      return;
    }
    nav(loc.state?.from?.pathname || "/", { replace: true });
  };

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5" data-testid="login-page">
      {/* Visual side */}
      <div className="relative hidden lg:block lg:col-span-3 overflow-hidden bg-ps-deep text-white">
        <img
          src="https://www.portside.nl/truck-togo.jpeg"
          alt="Portside trucks at the Lomé depot"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        {/* Gradient overlay (light, no full black) */}
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(0,59,92,0.78) 0%, rgba(0,111,158,0.55) 60%, rgba(0,111,158,0.35) 100%)" }} />

        <div className="relative h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <img src="https://www.portside.nl/portside-logo-rgb.svg" alt="Portside" className="w-12 h-12 bg-white p-1 rounded-md" />
            <div>
              <div className="font-display font-extrabold text-xl tracking-tight">PORTSIDE</div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-cyan-200">Global Shipping &amp; West Africa Logistics</div>
            </div>
          </div>

          <div className="max-w-xl">
            <div className="text-[11px] uppercase tracking-[0.3em] text-cyan-200 mb-3">Yard Operations Portal</div>
            <h1 className="font-display font-extrabold text-4xl xl:text-5xl tracking-tight leading-[1.05]">
              Navigating West Africa with global precision.
            </h1>
            <p className="mt-5 text-cyan-100 text-base leading-relaxed max-w-md">
              Internal console for the yard fuel station and on-the-road driver cash — built on the data your teams already work with every day across Tema, Lomé, and Cotonou.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-x-8 gap-y-4 max-w-md">
              {[
                ["40,000 L", "Reservoir capacity"],
                ["GHS", "Working currency"],
                ["Tema · Lomé", "Primary corridors"],
                ["24 / 7", "Operational visibility"],
              ].map(([k, v]) => (
                <div key={v}>
                  <div className="font-display font-extrabold text-2xl text-white tracking-tight">{k}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-cyan-200/80 mt-0.5">{v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="overflow-hidden whitespace-nowrap border-t border-white/20 pt-4 text-[11px] uppercase tracking-[0.3em] text-cyan-100/80 font-mono-data">
            <div className="marquee-track inline-block">
              <span className="mr-12">TEMA · GPHA · MPS · KUMASI · LOMÉ · ABIDJAN · COTONOU · OUAGADOUGOU · BOBO-DIOULASSO · DEPOT · FREEZONE</span>
              <span className="mr-12">TEMA · GPHA · MPS · KUMASI · LOMÉ · ABIDJAN · COTONOU · OUAGADOUGOU · BOBO-DIOULASSO · DEPOT · FREEZONE</span>
            </div>
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-8 lg:p-12 bg-white lg:col-span-2">
        <form onSubmit={onSubmit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <img src="https://www.portside.nl/portside-logo-rgb.svg" alt="Portside" className="w-10 h-10" />
            <div className="font-display font-extrabold text-lg tracking-tight text-ps-deep">PORTSIDE</div>
          </div>

          <div className="mb-8">
            <div className="text-[11px] uppercase tracking-[0.3em] text-ps-blue font-semibold">Admin access</div>
            <h2 className="font-display font-extrabold text-4xl tracking-tight mt-2 text-ps-deep">Sign in</h2>
            <p className="text-sm text-slate-600 mt-3 leading-relaxed">Single-operator console for the fuel station ledger and driver-cash workflow.</p>
          </div>

          {/* Google SSO for @portside.nl / @portside.ch teams */}
          <button
            type="button"
            onClick={startGoogle}
            data-testid="login-google-button"
            className="w-full flex items-center justify-center gap-3 border border-slate-300 rounded-md px-3 py-3 mb-5 text-[15px] font-semibold text-ps-deep bg-white hover:bg-slate-50 hover:border-ps-blue transition-colors shadow-sm"
          >
            <GoogleLogo size={20} weight="bold" className="text-ps-blue" />
            <span>Continue with Google</span>
          </button>
          <p className="text-[11px] text-slate-500 leading-relaxed mb-2">
            Sign in with your <span className="font-semibold text-ps-deep">@portside.nl</span> or <span className="font-semibold text-ps-deep">@portside.ch</span> Google account. Access must be provisioned by an admin first.
          </p>

          {!showBreakGlass ? (
            <button
              type="button"
              onClick={() => setShowBreakGlass(true)}
              data-testid="login-show-break-glass"
              className="mt-3 text-[11px] text-slate-400 hover:text-ps-blue uppercase tracking-[0.2em] underline-offset-2 hover:underline"
            >
              Admin break-glass sign-in →
            </button>
          ) : (
            <div className="mt-5 pt-5 border-t border-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <Lock size={14} className="text-amber-600" />
                <div className="text-[11px] uppercase tracking-[0.2em] text-amber-700 font-bold">Break-glass admin</div>
              </div>
              <p className="text-[11px] text-slate-500 leading-relaxed mb-3">
                Reserved for the admin account when Google sign-in is unavailable. Operators must use Google.
              </p>
              <label className="block text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">Email</label>
              <input
                type="email"
                required={showBreakGlass}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="w-full border border-slate-300 rounded-md px-3 py-2.5 mb-4 text-[15px] transition-colors"
              />

              <label className="block text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">Password</label>
              <div className="relative mb-2">
                <input
                  type="password"
                  required={showBreakGlass}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="w-full border border-slate-300 rounded-md px-3 py-2.5 pr-10 text-[15px] transition-colors"
                />
                <Lock size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>
            </div>
          )}

          {err ? (
            <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2 mt-3 rounded-md" data-testid="login-error">
              {err}
            </div>
          ) : null}

          {showBreakGlass && (
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="mt-6 w-full bg-ps-blue text-white font-semibold py-3 px-4 rounded-md hover:bg-ps-deep transition-colors duration-150 disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm"
            >
              {loading ? "Signing in…" : (<><span>Enter portal (admin only)</span><ArrowRight size={16} weight="bold" /></>)}
            </button>
          )}

          <div className="mt-10 pt-6 border-t border-slate-200 text-xs text-slate-500 leading-relaxed">
            &copy; {new Date().getFullYear()} Portside · The Netherlands · Ghana · Togo · Bénin
          </div>
        </form>
      </div>
    </div>
  );
}
